import { Text, type TextProps, type TextStyle } from 'react-native';

import { type TypographyVariant } from '@/constants/typography';
import { useFontScale } from '@/hooks/use-font-scale';
import { useThemeColor } from '@/hooks/use-theme-color';

export type ThemedTextProps = TextProps & {
  lightColor?: string;
  darkColor?: string;
  type?: 'default' | 'title' | 'defaultSemiBold' | 'subtitle' | 'link';
};

export function ThemedText({
  style,
  lightColor,
  darkColor,
  type = 'default',
  ...rest
}: ThemedTextProps) {
  const color = useThemeColor({ light: lightColor, dark: darkColor }, 'text');
  const primaryColor = useThemeColor({}, 'primary');
  const { getScaledTypography } = useFontScale();

  const variant = TYPE_TO_VARIANT[type] ?? 'body';
  const baseTypography = getScaledTypography(variant);
  const typeSpecificStyle = getTypeSpecificStyle(type, baseTypography, primaryColor);

  return (
    <Text
      style={[
        baseTypography,
        { color },
        typeSpecificStyle,
        style,
      ]}
      {...rest}
    />
  );
}

const TYPE_TO_VARIANT: Record<
  NonNullable<ThemedTextProps['type']>,
  TypographyVariant
> = {
  default: 'body',
  defaultSemiBold: 'body',
  title: 'title',
  subtitle: 'subheading',
  link: 'body',
};

const getTypeSpecificStyle = (
  type: ThemedTextProps['type'],
  typography: TextStyle,
  primaryColor: string
): TextStyle | undefined => {
  if (type === 'defaultSemiBold') {
    return { fontWeight: '600' };
  }

  if (type === 'link') {
    return {
      color: primaryColor,
      lineHeight: Math.max(
        typeof typography.lineHeight === 'number'
          ? typography.lineHeight
          : 0,
        typeof typography.fontSize === 'number'
          ? typography.fontSize + 6
          : 20
      ),
    };
  }

  return undefined;
};
